import './index.css';
import { StrictMode } from 'react';
import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import App from './App';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DashboardLayout from './components/DashboardLayout';
import DashboardOverview from './pages/dashboard/Overview';
import DashboardHistory from './pages/dashboard/History';
import DashboardAnalytics from './pages/dashboard/Analytics';
import DashboardSettings from './pages/dashboard/Settings';
import DashboardImpact from './pages/dashboard/Impact';
import DashboardGoals from './pages/dashboard/Goals';
import DashboardInsights from './pages/dashboard/Insights';
import DashboardTimeline from './pages/dashboard/Timeline';
import DashboardNotifications from './pages/dashboard/Notifications';
import DashboardIntegrations from './pages/dashboard/Integrations';
import DashboardReferrals from './pages/dashboard/Referrals';
import Home from './pages/Home';
import Compress from './pages/Compress';
import About from './pages/About';
import Features from './pages/Features';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Api from './pages/Api';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SlimFileSDGs from './pages/SlimFileSDGs';
import Partnerships from './pages/Partnerships';
import News from './pages/News';
import NewsArticle from './pages/NewsArticle';
import ProtectedRoute from './components/ProtectedRoute';
import SignInRequired from './components/SignInRequired';
import { Header } from './components/Header';

const HeaderOnlyLayout = () => (
  <>
    <Header />
    <Outlet />
  </>
);

const WithHeader = ({ children }: { children: React.ReactNode }) => (
  <>
    <Header />
    {children}
  </>
);
import { PenTool, FileText, Users } from 'lucide-react';

const WHITEBOARD_PITCH = 'Sketch ideas, plan projects and brainstorm on an endless canvas. Your boards are saved to your account so you can pick up where you left off.';
const WHITEBOARD_BENEFITS = ['Free to use', 'Saved automatically as you draw', 'Open them on any device'];
const WHITEBOARD_SEO = {
  title: 'Free Online Whiteboard — Sketch, Plan and Brainstorm | SlimFile',
  description: 'A free online whiteboard for sketching ideas, diagrams and plans on an endless canvas. Boards save automatically to your SlimFile account.',
};
const SLIMDOCX_PITCH = 'Write in a full Word-style editor with pages, tables and track changes. Your documents are saved to your account, so you can pick up where you left off.';
const SLIMDOCX_BENEFITS = ['Free to use', 'Open and download Word files', 'Saves automatically'];
const SLIMDOCX_SEO = {
  title: 'SlimDocx — Free Online Word Editor | SlimFile',
  description: 'Write and edit Word documents online for free with SlimDocx: pages, tables, track changes, and download as Word or PDF. Powered by ONLYOFFICE.',
};
const WORKSPACES_PITCH = 'Share files, plan work and collaborate with your team in one shared space.';
const WORKSPACES_BENEFITS = ['Free to use', 'Invite your team', 'Everything in one place'];
const WORKSPACES_SEO = {
  title: 'Team Workspaces — Collaborate on Files Together | SlimFile',
  description: 'Free team workspaces on SlimFile: share files, plan projects and work together with your team in one place.',
};
import FileCompressionEducation from './pages/FileCompressionEducation';
import CompressPdfOnline from './pages/CompressPdfOnline';
import CompressPptxOnline from './pages/CompressPptxOnline';
import CompressImagesOnline from './pages/CompressImagesOnline';
import CompressPdfForEmail from './pages/CompressPdfForEmail';
import CompressPdfOnIphone from './pages/CompressPdfOnIphone';
import CompressPdfWithoutLosingQuality from './pages/CompressPdfWithoutLosingQuality';
import CompressPptxForPresentation from './pages/CompressPptxForPresentation';
import CompressImagesForWebsite from './pages/CompressImagesForWebsite';
import ConvertOnly from './pages/ConvertOnly';
import ConvertThenCompress from './pages/ConvertThenCompress';
import GetStarted from './pages/GetStarted';

// Blog Pages
// Hidden SEO posts: routed and in the sitemap, but not listed on /blog.
const HiddenAdobeAcrobatAlternatives = lazy(() => import('./pages/blog/AdobeAcrobatAlternatives'));
const HiddenBatchCompressPdfs = lazy(() => import('./pages/blog/BatchCompressPdfs'));
const HiddenBestPdfCompressors2026 = lazy(() => import('./pages/blog/BestPdfCompressors2026'));
const HiddenCompressPdfForBusiness = lazy(() => import('./pages/blog/CompressPdfForBusiness'));
const HiddenCompressPdfForGmail = lazy(() => import('./pages/blog/CompressPdfForGmail'));
const HiddenCompressPdfForLinkedin = lazy(() => import('./pages/blog/CompressPdfForLinkedin'));
const HiddenCompressPdfForStudents = lazy(() => import('./pages/blog/CompressPdfForStudents'));
const HiddenCompressPdfForWhatsapp = lazy(() => import('./pages/blog/CompressPdfForWhatsapp'));
const HiddenCompressPdfOnlineFree = lazy(() => import('./pages/blog/CompressPdfOnlineFree'));
const HiddenCompressPdfWithoutLosingQuality = lazy(() => import('./pages/blog/CompressPdfWithoutLosingQuality'));
const HiddenCompressPptxOnlineFree = lazy(() => import('./pages/blog/CompressPptxOnlineFree'));
const HiddenHowToCompressMultiplePdfs = lazy(() => import('./pages/blog/HowToCompressMultiplePdfs'));
const HiddenHowToMergePdfFiles = lazy(() => import('./pages/blog/HowToMergePdfFiles'));
const HiddenHowToSplitPdfFiles = lazy(() => import('./pages/blog/HowToSplitPdfFiles'));
const HiddenILovePdfAlternatives = lazy(() => import('./pages/blog/ILovePdfAlternatives'));
const HiddenPdfCompressorForMac = lazy(() => import('./pages/blog/PdfCompressorForMac'));
const HiddenPdfCompressorForWindows = lazy(() => import('./pages/blog/PdfCompressorForWindows'));
const HiddenReducePdfFileSize = lazy(() => import('./pages/blog/ReducePdfFileSize'));
const HiddenWhatIsPdfCompression = lazy(() => import('./pages/blog/WhatIsPdfCompression'));
import BlogHowToCompressPdfForEmail from './pages/blog/HowToCompressPdfForEmail';
import BlogBestToolsToCompressImagesOnline from './pages/blog/BestToolsToCompressImagesOnline';
import BlogCompressPptxPresentationsWithoutLosingQuality from './pages/blog/CompressPptxPresentationsWithoutLosingQuality';
import BlogReducePdfFileSizeOnMobile from './pages/blog/ReducePdfFileSizeOnMobile';
import BlogHowToCompressFilesForFasterWebsiteLoading from './pages/blog/HowToCompressFilesForFasterWebsiteLoading';
import ScrollToTop from './components/ScrollToTop';
import BlogWhyFileCompressionIsEssentialForRemoteWork from './pages/blog/WhyFileCompressionIsEssentialForRemoteWork';
import { NotificationService } from './services/notificationService';
import BlogCompressingPdfForPrintingVsEmailing from './pages/blog/CompressingPdfForPrintingVsEmailing';
import BlogHowToCompressLargeVideosBeforeUploading from './pages/blog/HowToCompressLargeVideosBeforeUploading';
import BlogConvertJpgToPdf from './pages/blog/ConvertJpgToPdf';
import BlogConvertPngToWebp from './pages/blog/ConvertPngToWebp';
import BlogConvertPdfToWord from './pages/blog/ConvertPdfToWord';
import BlogConvertPdfToImage from './pages/blog/ConvertPdfToImage';
import BlogConvertJpgToPng from './pages/blog/ConvertJpgToPng';
import BlogConvertPngToJpg from './pages/blog/ConvertPngToJpg';
import BlogConvertWebpToJpg from './pages/blog/ConvertWebpToJpg';
import BlogConvertDocxToPdf from './pages/blog/ConvertDocxToPdf';
import BlogConvertPdfToPptx from './pages/blog/ConvertPdfToPptx';
import BlogConvertPdfToXlsx from './pages/blog/ConvertPdfToXlsx';

// New Blog Pages — Batch 1: PDF Compression
import BlogCompressPdfWithoutAdobe from './pages/blog/CompressPdfWithoutAdobe';
import BlogReducePdfSizeBelow1MB from './pages/blog/ReducePdfSizeBelow1MB';
import BlogBatchCompressMultiplePdfs from './pages/blog/BatchCompressMultiplePdfs';
import BlogCompressScannedPdf from './pages/blog/CompressScannedPdf';
import BlogWhyIsMypdfSoLarge from './pages/blog/WhyIsMypdfSoLarge';
import BlogCompressPdfInChrome from './pages/blog/CompressPdfInChrome';
import BlogCompressPdfForPrinting from './pages/blog/CompressPdfForPrinting';

// New Blog Pages — Batch 2: Image Compression
import BlogCompressImagesForInstagram from './pages/blog/CompressImagesForInstagram';
import BlogCompressImagesForFacebook from './pages/blog/CompressImagesForFacebook';
import BlogCompressImagesForWordpress from './pages/blog/CompressImagesForWordpress';
import BlogCompressImagesForShopify from './pages/blog/CompressImagesForShopify';
import BlogCompressImagesForEmail from './pages/blog/CompressImagesForEmail';
import BlogReducePhotoSizeOnIphone from './pages/blog/ReducePhotoSizeOnIphone';
import BlogReducePhotoSizeOnAndroid from './pages/blog/ReducePhotoSizeOnAndroid';
import BlogCompressGifFilesOnline from './pages/blog/CompressGifFilesOnline';

// New Blog Pages — Batch 3: Office Documents
import BlogCompressWordDocumentOnline from './pages/blog/CompressWordDocumentOnline';
import BlogCompressExcelFiles from './pages/blog/CompressExcelFiles';
import BlogCompressPowerpointWithoutLosingQuality from './pages/blog/CompressPowerpointWithoutLosingQuality';
import BlogReduceWordFileSize from './pages/blog/ReduceWordFileSize';
import BlogCompressFilesOnWindows11 from './pages/blog/CompressFilesOnWindows11';

// New Blog Pages — Batch 4: File Sharing
import BlogCompressFilesForWhatsapp from './pages/blog/CompressFilesForWhatsapp';
import BlogCompressFilesForGoogleDrive from './pages/blog/CompressFilesForGoogleDrive';
import BlogCompressFilesForSlack from './pages/blog/CompressFilesForSlack';
import BlogCompressFilesForDropbox from './pages/blog/CompressFilesForDropbox';
import BlogSendLargeFilesViaEmail from './pages/blog/SendLargeFilesViaEmail';
import BlogShareLargePdfFilesOnline from './pages/blog/ShareLargePdfFilesOnline';

// New Blog Pages — Batch 5: PDF Conversion
import BlogConvertWordToPdf from './pages/blog/ConvertWordToPdf';
import BlogConvertExcelToPdf from './pages/blog/ConvertExcelToPdf';
import BlogConvertPngToPdf from './pages/blog/ConvertPngToPdf';
import BlogConvertPdfToPng from './pages/blog/ConvertPdfToPng';
import BlogConvertPptxToPdf from './pages/blog/ConvertPptxToPdf';
import BlogConvertPdfToJpeg from './pages/blog/ConvertPdfToJpeg';
import BlogConvertPdfToText from './pages/blog/ConvertPdfToText';
import BlogConvertHeicToJpg from './pages/blog/ConvertHeicToJpg';

// New Blog Pages — Batch 6: Tool How-Tos
import BlogExtractTextFromPdfUsingOcr from './pages/blog/ExtractTextFromPdfUsingOcr';
import BlogMergePdfFilesOnlineFree from './pages/blog/MergePdfFilesOnlineFree';
import BlogSplitPdfIntoSeparatePages from './pages/blog/SplitPdfIntoSeparatePages';
import BlogPasswordProtectPdf from './pages/blog/PasswordProtectPdf';
import BlogRemovePasswordFromPdf from './pages/blog/RemovePasswordFromPdf';
import BlogSummarizePdfWithAi from './pages/blog/SummarizePdfWithAi';
import BlogExtractPagesFromPdf from './pages/blog/ExtractPagesFromPdf';
import BlogRotatePdfPagesOnline from './pages/blog/RotatePdfPagesOnline';

// New Blog Pages — Batch 7: Comparisons
import BlogBestFreeAlternativesToAdobeAcrobat from './pages/blog/BestFreeAlternativesToAdobeAcrobat';
import BlogSlimfileVsIlovepdf from './pages/blog/SlimfileVsIlovepdf';
import BlogSlimfileVsSmallpdf from './pages/blog/SlimfileVsSmallpdf';
import BlogBestFreePdfCompressorOnline from './pages/blog/BestFreePdfCompressorOnline';
import BlogBestFreeImageCompressorOnline from './pages/blog/BestFreeImageCompressorOnline';
import BlogBestOcrToolsOnline from './pages/blog/BestOcrToolsOnline';
import BlogBestAiDocumentSummarizersFree from './pages/blog/BestAiDocumentSummarizersFree';
import BlogBestPdfToolsForStudents from './pages/blog/BestPdfToolsForStudents';

// New Blog Pages — Batch 8: Educational / Evergreen
import BlogLosslessVsLossyCompression from './pages/blog/LosslessVsLossyCompression';
import BlogWhatIsAPdf from './pages/blog/WhatIsAPdf';
import BlogWhatIsWebp from './pages/blog/WhatIsWebp';
import BlogWhatIsOcr from './pages/blog/WhatIsOcr';
import BlogHowDpiAffectsPdfFileSize from './pages/blog/HowDpiAffectsPdfFileSize';
import BlogUnderstandingPdfCompressionAlgorithms from './pages/blog/UnderstandingPdfCompressionAlgorithms';
import BlogWhyFileCompressionMattersForSeo from './pages/blog/WhyFileCompressionMattersForSeo';

import OurBlogs from './pages/OurBlogs';
import Feed from './pages/Feed';
import Portals from './pages/Portals';
import GlobalDashboard from './pages/GlobalDashboard';
import Workspaces from './pages/Workspaces';
import SummitRegistration from './pages/SummitRegistration';
import WorkspaceDetail from './pages/WorkspaceDetail';
import WorkspaceInvitations from './pages/WorkspaceInvitations';
import OCRTool from './pages/OCRTool';
import PersonalWhiteboards from './pages/PersonalWhiteboards';
// Excalidraw is large, so the board editor loads only when a board is opened.
const PersonalWhiteboardCanvas = lazy(() => import('./pages/PersonalWhiteboardCanvas'));
import Documents from './pages/Documents';
// BlockNote is large, so the document editor loads only when a document is opened.
const DocumentEditor = lazy(() => import('./pages/DocumentEditor'));
import AdminNewsletter from './pages/AdminNewsletter';
import ReviewEmails from './pages/admin/ReviewEmails';
import SlimFileDrive from './pages/SlimFileDrive';
import Messages from './pages/Messages';
import PushListener from './components/PushListener';
import SlimFileForge from './pages/SlimFileForge';
import SlimFileMergeCompress from './pages/SlimFileMergeCompress';
import SlimFileSplitCompress from './pages/SlimFileSplitCompress';
import SlimFileScanCompress from './pages/SlimFileScanCompress';
import SlimFileScanMerge from './pages/SlimFileScanMerge';
import SlimFileScanSplit from './pages/SlimFileScanSplit';
import SlimFileScanConvert from './pages/SlimFileScanConvert';
import SlimFileLock from './pages/SlimFileLock';
import SlimFileZip from './pages/SlimFileZip';
import SlimFileQR from './pages/SlimFileQR';
import TextToPdf from './pages/TextToPdf';
import AILab from './pages/AILab';
import Reviews from './pages/Reviews';
import CommunityManager from './pages/CommunityManager';
import SecurityTerminal from './pages/SecurityTerminal';
import Leaderboard from './pages/Leaderboard';
import B2BApiKeysAdmin from './pages/B2BApiKeysAdmin';
import CEODashboard from './pages/CEODashboard';
import CampusDashboard from './pages/CampusDashboard';
import { trackCampusVisit } from './lib/campus';
import { NotificationProvider } from './components/InAppNotification';
import UpdatePrompt from './components/UpdatePrompt';

// Image Processing Pages
import ResizeImage from './pages/images/ResizeImage';
import CropImage from './pages/images/CropImage';
import WatermarkImage from './pages/images/WatermarkImage';
import RemoveBackground from './pages/images/RemoveBackground';
import ReplaceBackground from './pages/images/ReplaceBackground';
import BlurFaces from './pages/images/BlurFaces';
import EnhanceImage from './pages/images/EnhanceImage';
import GenerateThumbnails from './pages/images/GenerateThumbnails';

const root = document.getElementById('root');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
});

// PWA auto-refresh test deployment - v1.0.1

// A visit to slim-file.com counts for a campus once a day, on any page.
trackCampusVisit();

ReactDOM.createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <NotificationProvider>
            <ScrollToTop />
            <PushListener />
            <UpdatePrompt />
            <Routes>
        <Route path="/login" element={<Login />} />
        {/* New Dashboard with nested routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<DashboardOverview />} />
          <Route path="history" element={<DashboardHistory />} />
          <Route path="analytics" element={<DashboardAnalytics />} />
          <Route path="impact" element={<DashboardImpact />} />
          <Route path="goals" element={<DashboardGoals />} />
          <Route path="insights" element={<DashboardInsights />} />
          <Route path="timeline" element={<DashboardTimeline />} />
          <Route path="notifications" element={<DashboardNotifications />} />
          <Route path="integrations" element={<DashboardIntegrations />} />
          <Route path="referrals" element={<DashboardReferrals />} />
          <Route path="settings" element={<DashboardSettings />} />
        </Route>
        {/* Old dashboard route - keeping for backwards compatibility */}
        <Route path="/dashboard-old" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/" element={<App />}>
          <Route index element={<Home />} />
          <Route path="get-started" element={<GetStarted />} />
          <Route path="compress" element={<Compress />} />
          <Route path="convert-only" element={<ConvertOnly />} />
          <Route path="convert-compress" element={<ConvertThenCompress />} />
          <Route path="about" element={<About />} />
          <Route path="features" element={<Features />} />
          <Route path="contact" element={<Contact />} />
          <Route path="api" element={<Api />} />
          <Route path="file-compression-education" element={<FileCompressionEducation />} />
          {/* SEO Landing Pages */}
          <Route path="compress-pdf-online" element={<CompressPdfOnline />} />
          <Route path="compress-pptx-online" element={<CompressPptxOnline />} />
          <Route path="compress-images-online" element={<CompressImagesOnline />} />
          <Route path="compress-pdf-for-email" element={<CompressPdfForEmail />} />
          <Route path="compress-pdf-on-iphone" element={<CompressPdfOnIphone />} />
          <Route path="compress-pdf-without-losing-quality" element={<CompressPdfWithoutLosingQuality />} />
          <Route path="compress-pptx-for-presentation" element={<CompressPptxForPresentation />} />
          <Route path="compress-images-for-website" element={<CompressImagesForWebsite />} />

          {/* Blog Pages */}
          {/* Hidden SEO posts: not listed on /blog */}
          <Route path="blog/adobe-acrobat-alternatives" element={<Suspense fallback={null}><HiddenAdobeAcrobatAlternatives /></Suspense>} />
          <Route path="blog/batch-compress-pdfs" element={<Suspense fallback={null}><HiddenBatchCompressPdfs /></Suspense>} />
          <Route path="blog/best-pdf-compressors-2026" element={<Suspense fallback={null}><HiddenBestPdfCompressors2026 /></Suspense>} />
          <Route path="blog/compress-pdf-for-business" element={<Suspense fallback={null}><HiddenCompressPdfForBusiness /></Suspense>} />
          <Route path="blog/compress-pdf-for-gmail" element={<Suspense fallback={null}><HiddenCompressPdfForGmail /></Suspense>} />
          <Route path="blog/compress-pdf-for-linkedin" element={<Suspense fallback={null}><HiddenCompressPdfForLinkedin /></Suspense>} />
          <Route path="blog/compress-pdf-for-students" element={<Suspense fallback={null}><HiddenCompressPdfForStudents /></Suspense>} />
          <Route path="blog/compress-pdf-for-whatsapp" element={<Suspense fallback={null}><HiddenCompressPdfForWhatsapp /></Suspense>} />
          <Route path="blog/compress-pdf-online-free" element={<Suspense fallback={null}><HiddenCompressPdfOnlineFree /></Suspense>} />
          <Route path="blog/compress-pdf-without-losing-quality" element={<Suspense fallback={null}><HiddenCompressPdfWithoutLosingQuality /></Suspense>} />
          <Route path="blog/compress-pptx-online-free" element={<Suspense fallback={null}><HiddenCompressPptxOnlineFree /></Suspense>} />
          <Route path="blog/how-to-compress-multiple-pdfs" element={<Suspense fallback={null}><HiddenHowToCompressMultiplePdfs /></Suspense>} />
          <Route path="blog/how-to-merge-pdf-files" element={<Suspense fallback={null}><HiddenHowToMergePdfFiles /></Suspense>} />
          <Route path="blog/how-to-split-pdf-files" element={<Suspense fallback={null}><HiddenHowToSplitPdfFiles /></Suspense>} />
          <Route path="blog/ilovepdf-alternatives" element={<Suspense fallback={null}><HiddenILovePdfAlternatives /></Suspense>} />
          <Route path="blog/pdf-compressor-for-mac" element={<Suspense fallback={null}><HiddenPdfCompressorForMac /></Suspense>} />
          <Route path="blog/pdf-compressor-for-windows" element={<Suspense fallback={null}><HiddenPdfCompressorForWindows /></Suspense>} />
          <Route path="blog/reduce-pdf-file-size" element={<Suspense fallback={null}><HiddenReducePdfFileSize /></Suspense>} />
          <Route path="blog/what-is-pdf-compression" element={<Suspense fallback={null}><HiddenWhatIsPdfCompression /></Suspense>} />
          <Route path="blog/how-to-compress-pdf-for-email" element={<BlogHowToCompressPdfForEmail />} />
          <Route path="blog/best-tools-to-compress-images-online" element={<BlogBestToolsToCompressImagesOnline />} />
          <Route path="blog/compress-pptx-presentations-without-losing-quality" element={<BlogCompressPptxPresentationsWithoutLosingQuality />} />
          <Route path="blog/reduce-pdf-file-size-on-mobile" element={<BlogReducePdfFileSizeOnMobile />} />
          <Route path="blog/how-to-compress-files-for-faster-website-loading" element={<BlogHowToCompressFilesForFasterWebsiteLoading />} />
          <Route path="blog/why-file-compression-is-essential-for-remote-work" element={<BlogWhyFileCompressionIsEssentialForRemoteWork />} />
          <Route path="blog/compressing-pdf-for-printing-vs-emailing" element={<BlogCompressingPdfForPrintingVsEmailing />} />
          <Route path="blog/how-to-compress-large-videos-before-uploading" element={<BlogHowToCompressLargeVideosBeforeUploading />} />
          <Route path="blog/convert-jpg-to-pdf" element={<BlogConvertJpgToPdf />} />
          <Route path="blog/convert-png-to-webp" element={<BlogConvertPngToWebp />} />
          <Route path="blog/convert-pdf-to-word" element={<BlogConvertPdfToWord />} />
          <Route path="blog/convert-pdf-to-image" element={<BlogConvertPdfToImage />} />
          <Route path="blog/convert-jpg-to-png" element={<BlogConvertJpgToPng />} />
          <Route path="blog/convert-png-to-jpg" element={<BlogConvertPngToJpg />} />
          <Route path="blog/convert-webp-to-jpg" element={<BlogConvertWebpToJpg />} />
          <Route path="blog/convert-docx-to-pdf" element={<BlogConvertDocxToPdf />} />
          <Route path="blog/convert-pdf-to-pptx" element={<BlogConvertPdfToPptx />} />
          <Route path="blog/convert-pdf-to-xlsx" element={<BlogConvertPdfToXlsx />} />

          {/* New Blog Routes — Batch 1: PDF Compression */}
          <Route path="blog/compress-pdf-without-adobe" element={<BlogCompressPdfWithoutAdobe />} />
          <Route path="blog/reduce-pdf-size-below-1mb" element={<BlogReducePdfSizeBelow1MB />} />
          <Route path="blog/batch-compress-multiple-pdfs" element={<BlogBatchCompressMultiplePdfs />} />
          <Route path="blog/compress-scanned-pdf" element={<BlogCompressScannedPdf />} />
          <Route path="blog/why-is-my-pdf-so-large" element={<BlogWhyIsMypdfSoLarge />} />
          <Route path="blog/compress-pdf-in-chrome" element={<BlogCompressPdfInChrome />} />
          <Route path="blog/compress-pdf-for-printing" element={<BlogCompressPdfForPrinting />} />

          {/* New Blog Routes — Batch 2: Image Compression */}
          <Route path="blog/compress-images-for-instagram" element={<BlogCompressImagesForInstagram />} />
          <Route path="blog/compress-images-for-facebook" element={<BlogCompressImagesForFacebook />} />
          <Route path="blog/compress-images-for-wordpress" element={<BlogCompressImagesForWordpress />} />
          <Route path="blog/compress-images-for-shopify" element={<BlogCompressImagesForShopify />} />
          <Route path="blog/compress-images-for-email" element={<BlogCompressImagesForEmail />} />
          <Route path="blog/reduce-photo-size-on-iphone" element={<BlogReducePhotoSizeOnIphone />} />
          <Route path="blog/reduce-photo-size-on-android" element={<BlogReducePhotoSizeOnAndroid />} />
          <Route path="blog/compress-gif-files-online" element={<BlogCompressGifFilesOnline />} />

          {/* New Blog Routes — Batch 3: Office Documents */}
          <Route path="blog/compress-word-document-online" element={<BlogCompressWordDocumentOnline />} />
          <Route path="blog/compress-excel-files" element={<BlogCompressExcelFiles />} />
          <Route path="blog/compress-powerpoint-without-losing-quality" element={<BlogCompressPowerpointWithoutLosingQuality />} />
          <Route path="blog/reduce-word-file-size" element={<BlogReduceWordFileSize />} />
          <Route path="blog/compress-files-on-windows-11" element={<BlogCompressFilesOnWindows11 />} />

          {/* New Blog Routes — Batch 4: File Sharing */}
          <Route path="blog/compress-files-for-whatsapp" element={<BlogCompressFilesForWhatsapp />} />
          <Route path="blog/compress-files-for-google-drive" element={<BlogCompressFilesForGoogleDrive />} />
          <Route path="blog/compress-files-for-slack" element={<BlogCompressFilesForSlack />} />
          <Route path="blog/compress-files-for-dropbox" element={<BlogCompressFilesForDropbox />} />
          <Route path="blog/send-large-files-via-email" element={<BlogSendLargeFilesViaEmail />} />
          <Route path="blog/share-large-pdf-files-online" element={<BlogShareLargePdfFilesOnline />} />

          {/* New Blog Routes — Batch 5: PDF Conversion */}
          <Route path="blog/convert-word-to-pdf" element={<BlogConvertWordToPdf />} />
          <Route path="blog/convert-excel-to-pdf" element={<BlogConvertExcelToPdf />} />
          <Route path="blog/convert-png-to-pdf" element={<BlogConvertPngToPdf />} />
          <Route path="blog/convert-pdf-to-png" element={<BlogConvertPdfToPng />} />
          <Route path="blog/convert-pptx-to-pdf" element={<BlogConvertPptxToPdf />} />
          <Route path="blog/convert-pdf-to-jpeg" element={<BlogConvertPdfToJpeg />} />
          <Route path="blog/convert-pdf-to-text" element={<BlogConvertPdfToText />} />
          <Route path="blog/convert-heic-to-jpg" element={<BlogConvertHeicToJpg />} />

          {/* New Blog Routes — Batch 6: Tool How-Tos */}
          <Route path="blog/extract-text-from-pdf-using-ocr" element={<BlogExtractTextFromPdfUsingOcr />} />
          <Route path="blog/merge-pdf-files-online-free" element={<BlogMergePdfFilesOnlineFree />} />
          <Route path="blog/split-pdf-into-separate-pages" element={<BlogSplitPdfIntoSeparatePages />} />
          <Route path="blog/password-protect-pdf" element={<BlogPasswordProtectPdf />} />
          <Route path="blog/remove-password-from-pdf" element={<BlogRemovePasswordFromPdf />} />
          <Route path="blog/summarize-pdf-with-ai" element={<BlogSummarizePdfWithAi />} />
          <Route path="blog/extract-pages-from-pdf" element={<BlogExtractPagesFromPdf />} />
          <Route path="blog/rotate-pdf-pages-online" element={<BlogRotatePdfPagesOnline />} />

          {/* New Blog Routes — Batch 7: Comparisons */}
          <Route path="blog/best-free-alternatives-to-adobe-acrobat" element={<BlogBestFreeAlternativesToAdobeAcrobat />} />
          <Route path="blog/slimfile-vs-ilovepdf" element={<BlogSlimfileVsIlovepdf />} />
          <Route path="blog/slimfile-vs-smallpdf" element={<BlogSlimfileVsSmallpdf />} />
          <Route path="blog/best-free-pdf-compressor-online" element={<BlogBestFreePdfCompressorOnline />} />
          <Route path="blog/best-free-image-compressor-online" element={<BlogBestFreeImageCompressorOnline />} />
          <Route path="blog/best-ocr-tools-online" element={<BlogBestOcrToolsOnline />} />
          <Route path="blog/best-ai-document-summarizers-free" element={<BlogBestAiDocumentSummarizersFree />} />
          <Route path="blog/best-pdf-tools-for-students" element={<BlogBestPdfToolsForStudents />} />

          {/* New Blog Routes — Batch 8: Educational */}
          <Route path="blog/lossless-vs-lossy-compression" element={<BlogLosslessVsLossyCompression />} />
          <Route path="blog/what-is-a-pdf" element={<BlogWhatIsAPdf />} />
          <Route path="blog/what-is-webp" element={<BlogWhatIsWebp />} />
          <Route path="blog/what-is-ocr" element={<BlogWhatIsOcr />} />
          <Route path="blog/how-dpi-affects-pdf-file-size" element={<BlogHowDpiAffectsPdfFileSize />} />
          <Route path="blog/understanding-pdf-compression-algorithms" element={<BlogUnderstandingPdfCompressionAlgorithms />} />
          <Route path="blog/why-file-compression-matters-for-seo" element={<BlogWhyFileCompressionMattersForSeo />} />

          <Route path="blog" element={<OurBlogs />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="sdgs" element={<SlimFileSDGs />} />
          <Route path="partnerships" element={<Partnerships />} />
          <Route path="news" element={<News />} />
          <Route path="news/:newsId" element={<NewsArticle />} />
          <Route path="summit-registration" element={<SummitRegistration />} />
          <Route path="global-dashboard" element={<GlobalDashboard />} />
          <Route path="feed" element={<Feed />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="community-manager" element={<CommunityManager />} />
          <Route path="security-terminal" element={<SecurityTerminal />} />
          <Route path="b2b-api-keys-admin" element={<B2BApiKeysAdmin />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="portals" element={
            <ProtectedRoute>
              <Portals />
            </ProtectedRoute>
          } />
          <Route path="workspaces" element={
            <SignInRequired feature="Workspaces" icon={Users} description={WORKSPACES_PITCH} benefits={WORKSPACES_BENEFITS} seo={WORKSPACES_SEO}>
              <Workspaces />
            </SignInRequired>
          } />
          <Route path="workspaces/invitations" element={
            <ProtectedRoute>
              <WorkspaceInvitations />
            </ProtectedRoute>
          } />
          <Route path="ocr-tool" element={<OCRTool />} />
          <Route path="forge" element={<SlimFileForge />} />
          <Route path="merge-compress" element={<SlimFileMergeCompress />} />
          <Route path="split-compress" element={<SlimFileSplitCompress />} />
          <Route path="scan-compress" element={<SlimFileScanCompress />} />
          <Route path="scan-merge" element={<SlimFileScanMerge />} />
          <Route path="scan-split" element={<SlimFileScanSplit />} />
          <Route path="scan-convert" element={<SlimFileScanConvert />} />
          <Route path="lock" element={<SlimFileLock />} />
          <Route path="zip" element={<SlimFileZip />} />
          <Route path="qr-code" element={<SlimFileQR />} />
          <Route path="text-to-pdf" element={<TextToPdf />} />
          <Route path="ai-lab" element={<AILab />} />

          {/* Image Processing Tools */}
          <Route path="images/resize" element={<ResizeImage />} />
          <Route path="images/crop" element={<CropImage />} />
          <Route path="images/watermark" element={<WatermarkImage />} />
          <Route path="images/remove-background" element={<RemoveBackground />} />
          <Route path="images/replace-background" element={<ReplaceBackground />} />
          <Route path="images/blur-faces" element={<BlurFaces />} />
          <Route path="images/enhance" element={<EnhanceImage />} />
          <Route path="images/thumbnails" element={<GenerateThumbnails />} />
        </Route>

        {/* Workspace chat - Full screen without header/footer */}
        <Route path="workspaces/:workspaceId" element={
          <ProtectedRoute>
            <WorkspaceDetail />
          </ProtectedRoute>
        } />

        {/* Whiteboards and documents are full-screen editors: they get the
            site header but not the footer or chat bubble of the main layout. */}
        <Route element={<HeaderOnlyLayout />}>
          {/* Personal whiteboards */}
          <Route path="my-whiteboards" element={
            <SignInRequired feature="Whiteboards" icon={PenTool} description={WHITEBOARD_PITCH} benefits={WHITEBOARD_BENEFITS} seo={WHITEBOARD_SEO}>
              <PersonalWhiteboards />
            </SignInRequired>
          } />

          {/* Personal whiteboard canvas */}
          <Route path="my-whiteboards/:whiteboardId" element={
            <SignInRequired feature="Whiteboards" icon={PenTool} description={WHITEBOARD_PITCH} benefits={WHITEBOARD_BENEFITS} seo={WHITEBOARD_SEO}>
              <Suspense fallback={null}>
                <PersonalWhiteboardCanvas />
              </Suspense>
            </SignInRequired>
          } />

          {/* Documents */}
          <Route path="documents" element={
            <SignInRequired feature="SlimDocx" icon={FileText} description={SLIMDOCX_PITCH} benefits={SLIMDOCX_BENEFITS} seo={SLIMDOCX_SEO}>
              <Documents />
            </SignInRequired>
          } />

          {/* Document Editor */}
          <Route path="documents/:documentId" element={
            <ProtectedRoute>
              <Suspense fallback={null}>
              <DocumentEditor />
            </Suspense>
            </ProtectedRoute>
          } />
        </Route>

        {/* Admin newsletter — protected by server-side email check */}
        <Route path="admin/newsletter" element={
          <ProtectedRoute>
            <WithHeader><AdminNewsletter /></WithHeader>
          </ProtectedRoute>
        } />

        {/* Admin Review Emails — protected by server-side email check */}
        <Route path="admin/review-emails" element={
          <ProtectedRoute>
            <WithHeader><ReviewEmails /></WithHeader>
          </ProtectedRoute>
        } />

        {/* SlimFile Drive */}
        <Route path="drive" element={
          <ProtectedRoute>
            <SlimFileDrive />
          </ProtectedRoute>
        } />

        {/* Messages - Full screen DM chat without header/footer */}
        <Route path="messages" element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        } />

        {/* CEO Dashboard - Full screen analytics dashboard without header/footer */}
        <Route path="ceo-dashboard" element={
          <ProtectedRoute>
            <WithHeader><CEODashboard /></WithHeader>
          </ProtectedRoute>
        } />
        <Route path="campuses" element={
          <ProtectedRoute>
            <WithHeader><CampusDashboard /></WithHeader>
          </ProtectedRoute>
        } />
            </Routes>
          </NotificationProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>
);

// Add Google Identity Services script
const script = document.createElement('script');
script.src = 'https://accounts.google.com/gsi/client';
script.async = true;
script.defer = true;
document.body.appendChild(script);
